import React from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Building2, FastForward, ArrowRight } from 'lucide-react';
import { titleCaseCompany } from '../../lib/nameCase';

export const DealerNameStep = ({ onSkipToReview, onAutoAdvance }) => {
  const { formData, extractedData } = useForm();
  const { updateFormData } = useForm();

  // Show skip to review if we have extracted data (regardless of completeness)
  // Missing fields will be highlighted on the review page
  const canSkipToReview = () => {
    if (!extractedData) return false;
    if (!formData.dealer_name?.trim()) return false;
    return true; // Allow skip as long as dealer name is entered and data was extracted
  };

  const canContinue = Boolean(formData.dealer_name?.trim());

  // Inline Next button — the global Next button is fixed to the bottom of the
  // viewport which on mobile sits below the on-screen keyboard once a text input
  // is focused, making it easy to miss on the very first step. Mirroring it here
  // directly under the input fixes that.

  return (
    <div className="space-y-6" data-testid="dealer-name-step">
      <div className="text-center">
        <div className="w-16 h-16 brand-bg-medium rounded-full hidden md:flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 brand-text" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dealer Name
        </h2>
        <p className="text-slate-600 hidden md:block">Please enter the supplying dealer name below</p>
      </div>

      <div className="max-w-md mx-auto">
        <Input
          id="dealer_name"
          placeholder="Enter dealer name"
          value={formData.dealer_name}
          onChange={(e) => updateFormData({ dealer_name: e.target.value })}
          onBlur={(e) => {
            const cased = titleCaseCompany(e.target.value);
            if (cased !== e.target.value) updateFormData({ dealer_name: cased });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canContinue && onAutoAdvance) {
              e.preventDefault();
              onAutoAdvance();
            }
          }}
          className="h-12 text-lg"
          autoFocus
          data-testid="dealer-name-input"
        />

        {onAutoAdvance && (
          <Button
            onClick={onAutoAdvance}
            disabled={!canContinue}
            className="mt-3 w-full h-12 brand-bg text-white font-semibold disabled:opacity-50"
            data-testid="dealer-name-next-btn"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {canSkipToReview() && (
          <div className="mt-6 p-4 brand-bg-light rounded-lg border brand-border-light">
            <p className="text-sm brand-text mb-3">
              ✓ Data extracted - you can skip to review (missing fields will be highlighted)
            </p>
            <Button
              onClick={onSkipToReview}
              className="w-full brand-bg  text-white font-semibold"
              data-testid="skip-to-review-btn"
            >
              <FastForward className="w-4 h-4 mr-2" />
              Skip to Review
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerNameStep;
